import React, { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Navigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Calendar, Clock, ArrowLeft, Phone, MapPin } from 'lucide-react';
import { brandTheme } from '../../styles/BrandTheme';
import { trackBlogView } from '../../utils/gtag';
import HeroSectionComponent from '../../components/HeroSection';
import {
  getTopicArticleBySlug,
  getRegionArticleBySlug,
  TOPIC_ARTICLES,
  REGION_ARTICLES,
  topicCanonical,
  regionCanonical,
  type SeoArticleDefinition,
} from '../../data/seoArticleLibrary';

const ArticleContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${brandTheme.colors.primary[600]};
  text-decoration: none;
  margin-bottom: 2rem;
  font-weight: 500;
  &:hover {
    color: ${brandTheme.colors.primary[700]};
  }
`;

const ArticleHeader = styled.header`
  margin-bottom: 2rem;
  .category {
    display: inline-block;
    background: ${brandTheme.colors.primary[100]};
    color: ${brandTheme.colors.primary[700]};
    padding: 0.5rem 1rem;
    border-radius: ${brandTheme.borderRadius.full};
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 1rem;
  }
  h1 {
    font-size: clamp(1.65rem, 4vw, 2.5rem);
    line-height: 1.2;
    margin-bottom: 1rem;
    color: ${brandTheme.colors.secondary[900]};
  }
  .excerpt {
    font-size: 1.1rem;
    color: ${brandTheme.colors.secondary[600]};
    line-height: 1.6;
  }
  .meta {
    display: flex;
    gap: 1.25rem;
    flex-wrap: wrap;
    margin-top: 1.25rem;
    color: ${brandTheme.colors.neutral.gray[500]};
    font-size: 0.95rem;
  }
`;

const ArticleBody = styled.div`
  font-size: 1.05rem;
  line-height: 1.8;
  color: ${brandTheme.colors.secondary[700]};
  h2 {
    font-size: 1.45rem;
    margin: 2.25rem 0 0.85rem;
    color: ${brandTheme.colors.secondary[900]};
  }
  p {
    margin-bottom: 1.1rem;
  }
  a {
    color: ${brandTheme.colors.primary[600]};
    font-weight: 500;
  }
`;

const TOC = styled.nav`
  background: ${brandTheme.colors.neutral.gray[50]};
  border: 1px solid ${brandTheme.colors.neutral.gray[200]};
  border-radius: ${brandTheme.borderRadius.lg};
  padding: 1.15rem 1.35rem;
  margin-bottom: 1.75rem;
  .toc-heading {
    font-size: 0.95rem;
    font-weight: 600;
    margin: 0 0 0.6rem 0;
    color: ${brandTheme.colors.secondary[900]};
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
  ol {
    margin: 0;
    padding-left: 1.2rem;
    font-size: 0.92rem;
    line-height: 1.65;
  }
  a {
    color: ${brandTheme.colors.primary[700]};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const CTA = styled.div`
  background: linear-gradient(135deg, ${brandTheme.colors.primary[600]}, ${brandTheme.colors.primary[800]});
  border-radius: ${brandTheme.borderRadius.xl};
  padding: 1.75rem;
  text-align: center;
  color: white;
  margin: 2.5rem 0;
  h3 {
    color: white;
    margin-bottom: 0.75rem;
    font-size: 1.2rem;
  }
  p {
    margin-bottom: 1rem;
    opacity: 0.95;
  }
  .cta-buttons {
    display: flex;
    gap: 0.75rem;
    justify-content: center;
    flex-wrap: wrap;
    a {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      padding: 0.65rem 1.5rem;
      border-radius: ${brandTheme.borderRadius.lg};
      text-decoration: none;
      font-weight: 600;
      &.primary {
        background: white;
        color: ${brandTheme.colors.primary[700]};
      }
      &.secondary {
        background: rgba(255, 255, 255, 0.15);
        color: white;
        border: 2px solid rgba(255, 255, 255, 0.35);
      }
    }
  }
`;

const FaqBlock = styled.section`
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${brandTheme.colors.neutral.gray[200]};
  h2 {
    font-size: 1.35rem;
    margin-bottom: 1rem;
  }
`;

const FaqItem = styled.div`
  margin-bottom: 1.25rem;
  strong {
    display: block;
    color: ${brandTheme.colors.secondary[900]};
    margin-bottom: 0.35rem;
  }
  p {
    margin: 0;
    color: ${brandTheme.colors.secondary[600]};
    font-size: 0.98rem;
  }
`;

type Mode = 'topic' | 'region';

interface Props {
  mode: Mode;
}

function buildJsonLd(article: SeoArticleDefinition, canonicalUrl: string, ogImage: string) {
  const siteOrigin = 'https://www.resinsurfaceslondon.co.uk';
  const iso = `${article.date}T09:00:00+00:00`;
  const ogFull = `${siteOrigin}${ogImage}`;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${siteOrigin}/#organization`,
        name: 'ZAN RESIN SURFACES LTD',
        url: siteOrigin,
        logo: { '@type': 'ImageObject', url: `${siteOrigin}/images/resin-surfaces-bound-work1.jpeg` },
        telephone: '+447535935779',
      },
      {
        '@type': 'WebPage',
        '@id': canonicalUrl,
        url: canonicalUrl,
        name: article.title,
        description: article.metaDescription,
        inLanguage: 'en-GB',
        primaryImageOfPage: { '@type': 'ImageObject', url: ogFull, width: 1200, height: 630 },
      },
      {
        '@type': 'BlogPosting',
        '@id': `${canonicalUrl}#article`,
        headline: article.title,
        description: article.metaDescription,
        url: canonicalUrl,
        datePublished: iso,
        dateModified: iso,
        author: { '@id': `${siteOrigin}/#organization` },
        publisher: { '@id': `${siteOrigin}/#organization` },
        image: { '@type': 'ImageObject', url: ogFull, width: 1200, height: 630 },
        mainEntityOfPage: { '@id': canonicalUrl },
        articleSection: article.category,
        keywords: article.keywords,
        inLanguage: 'en-GB',
        isAccessibleForFree: true,
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: 'h1, h2, #article-h1, #article-faq',
        },
      },
      {
        '@type': 'FAQPage',
        '@id': `${canonicalUrl}#faq`,
        mainEntity: article.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteOrigin + '/' },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: siteOrigin + '/blog/' },
          { '@type': 'ListItem', position: 3, name: article.title, item: canonicalUrl },
        ],
      },
    ],
  };
}

const SeoArticlePage: React.FC<Props> = ({ mode }) => {
  const { slug = '' } = useParams<{ slug: string }>();

  const article = useMemo(() => {
    return mode === 'topic' ? getTopicArticleBySlug(slug) : getRegionArticleBySlug(slug);
  }, [mode, slug]);

  useEffect(() => {
    if (article) trackBlogView(article.title);
  }, [article]);

  const jsonLd = useMemo(() => {
    if (!article) return null;
    const canonical = mode === 'topic' ? topicCanonical(article.slug) : regionCanonical(article.slug);
    return buildJsonLd(article, canonical, article.image);
  }, [article, mode]);

  const related = useMemo(() => {
    if (!article) return [];
    const pool = mode === 'topic' ? TOPIC_ARTICLES : REGION_ARTICLES;
    return pool.filter((a) => a.slug !== article.slug).slice(0, 4);
  }, [mode, article]);

  if (!article) {
    return <Navigate to="/blog/" replace />;
  }

  const canonicalUrl = mode === 'topic' ? topicCanonical(article.slug) : regionCanonical(article.slug);
  const ogImageAbs = `https://www.resinsurfaceslondon.co.uk${article.image}`;

  return (
    <>
      <Helmet>
        <title>{article.title} | ZAN RESIN SURFACES LTD</title>
        <meta name="description" content={article.metaDescription} />
        <meta name="keywords" content={article.keywords} />
        <meta name="author" content="ZAN RESIN SURFACES LTD" />
        <meta name="publisher" content="ZAN RESIN SURFACES LTD" />
        <meta name="language" content="en-GB" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImageAbs} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={article.title} />
        <meta property="og:site_name" content="ZAN RESIN SURFACES LTD" />
        <meta property="og:locale" content="en_GB" />
        <meta property="article:published_time" content={`${article.date}T09:00:00+00:00`} />
        <meta property="article:modified_time" content={`${article.date}T09:00:00+00:00`} />
        <meta property="article:author" content="ZAN RESIN SURFACES LTD" />
        <meta property="article:section" content={article.category} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.metaDescription} />
        <meta name="twitter:image" content={ogImageAbs} />
        <meta name="twitter:image:alt" content={article.title} />
        <meta name="twitter:site" content="@resinsurfaceslondon" />
        <meta name="twitter:creator" content="@resinsurfaceslondon" />
        <meta name="geo.region" content="GB-LND" />
        <meta name="geo.placename" content="London" />
        <link rel="alternate" hrefLang="en-GB" href={canonicalUrl} />
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
        {jsonLd ? <script type="application/ld+json">{JSON.stringify(jsonLd)}</script> : null}
      </Helmet>

      <HeroSectionComponent
        title={article.title}
        subtitle={article.excerpt}
        badge={article.category === 'Regions' ? 'County guide • Resin bound surfacing' : 'Expert guide • Resin bound surfacing'}
        backgroundImage={article.image}
        primaryButtonText="Get a quote"
        primaryButtonLink="/contact/"
        secondaryButtonText="Call"
        secondaryButtonLink="tel:+447535935779"
      />

      <ArticleContainer>
        <BackLink to="/blog/">
          <ArrowLeft size={18} />
          Back to Blog
        </BackLink>

        <article>
          <ArticleHeader>
            <span className="category">{article.category}</span>
            <h1 id="article-h1">{article.title}</h1>
            <p className="excerpt">{article.excerpt}</p>
            <div className="meta">
              <span>
                <Calendar size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                <time dateTime={article.date}>
                  {new Date(article.date + 'T12:00:00').toLocaleDateString('en-GB', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </time>
              </span>
              <span>
                <Clock size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                {article.readTime}
              </span>
              {article.category === 'Regions' && (
                <span>
                  <MapPin size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                  Home counties
                </span>
              )}
            </div>
          </ArticleHeader>

          <TOC aria-label="On this page">
            <p className="toc-heading">On this page</p>
            <ol>
              {article.sections.map((s) => (
                <li key={s.id}>
                  <a href={`#section-${s.id}`}>{s.heading}</a>
                </li>
              ))}
              <li>
                <a href="#article-faq">FAQ</a>
              </li>
            </ol>
          </TOC>

          <ArticleBody>
            {article.sections.map((s) => (
              <section key={s.id} id={`section-${s.id}`}>
                <h2>{s.heading}</h2>
                {s.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </section>
            ))}

            {article.servicePath && (
              <p>
                <Link to={article.servicePath}>View our full {article.title.split(':')[0].replace('County Guide', '').trim()} service area page</Link> for coverage, imagery and enquiry options.
              </p>
            )}

            <CTA>
              <h3>Request a free survey</h3>
              <p>We install resin bound surfacing across London and the home counties.</p>
              <div className="cta-buttons">
                <Link to="/contact/" className="primary">
                  Get a quote
                </Link>
                <a href="tel:+447535935779" className="secondary">
                  <Phone size={18} />
                  07535 935 779
                </a>
                {article.servicePath ? (
                  <Link to={article.servicePath} className="secondary">
                    Service area
                  </Link>
                ) : null}
              </div>
            </CTA>

            {related.length > 0 && (
              <section style={{ marginBottom: '2rem' }}>
                <h2 style={{ fontSize: '1.2rem', marginBottom: '0.75rem' }}>Related guides</h2>
                <ul style={{ lineHeight: 1.8 }}>
                  {related.map((r) => (
                    <li key={r.slug}>
                      <Link to={mode === 'topic' ? `/blog/guides/${r.slug}/` : `/blog/regions/${r.slug}/`}>
                        {r.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </ArticleBody>

          <FaqBlock id="article-faq">
            <h2>Frequently asked questions</h2>
            {article.faqs.map((f) => (
              <FaqItem key={f.q}>
                <strong>{f.q}</strong>
                <p>{f.a}</p>
              </FaqItem>
            ))}
          </FaqBlock>
        </article>
      </ArticleContainer>
    </>
  );
};

export default SeoArticlePage;
